import {useSidebar} from '@/contexts/SidebarContext/SidebarContext';
import {
  SIDEBAR_CATEGORY_LABELS,
  SidebarCategory,
} from '@/contexts/SidebarContext/types';
import {useMobileNavigation} from '@/contexts/MobileNavigationContext/MobileNavigationContext';
import {Box, Drawer, Typography} from '@mui/material';
import {tokens} from '@ark/web';
import {useTheme} from '@mui/material/styles';
import {useNavigate} from 'react-router-dom';

const MORE_CATEGORIES: SidebarCategory[] = [
  'reporting',
  'engagement',
  'bookings',
  'concierge',
  'tasks',
  'payments',
  'compliance',
  'safetyCenter',
  'admin',
  'onboarding',
];

export const MobileMoreSheet = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {isMoreSheetOpen, closeMoreSheet} = useMobileNavigation();
  const {getItemsForCategory} = useSidebar();

  const handleItemClick = (url: string) => {
    closeMoreSheet();
    navigate(url);
  };

  return (
    <Drawer
      anchor="bottom"
      open={isMoreSheetOpen}
      onClose={closeMoreSheet}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '85vh',
          pb: 'env(safe-area-inset-bottom)',
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.action.disabled,
          mx: 'auto',
          mt: 1.5,
          mb: 1,
        }}
      />
      <Box
        sx={{
          px: 2,
          pt: 1,
          pb: 3,
          overflowY: 'auto',
        }}
      >
        {MORE_CATEGORIES.map(category => {
          const items = getItemsForCategory(category);
          if (items.length === 0) return null;

          return (
            <Box key={category} sx={{mb: 2}}>
              <Typography
                sx={{
                  ...tokens.text.label,
                  textTransform: 'uppercase',
                  color: theme.palette.text.secondary,
                  px: 1.5,
                  mb: 1,
                }}
              >
                {SIDEBAR_CATEGORY_LABELS[category]}
              </Typography>
              {items
                .filter(item => item.type !== 'divider')
                .map(item => (
                  <Box
                    key={item.url}
                    onClick={() => handleItemClick(item.url)}
                    data-attr="mobile-more-sheet-item-open"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: tokens.spacing.gap.md / 8,
                      px: 1.5,
                      py: 1.25,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'transparent',
                      '&:active': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.text.secondary,
                        '& svg': {fontSize: 18},
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      sx={{
                        ...tokens.text.title,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {item.label}
                    </Typography>
                    {item.BadgeComponent && (
                      <item.BadgeComponent>
                        <Box />
                      </item.BadgeComponent>
                    )}
                  </Box>
                ))}
            </Box>
          );
        })}
      </Box>
    </Drawer>
  );
};
